import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
} from '@aurora/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api/client.js';

interface AuditItem {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  userLabel: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  createdAt: string;
}
interface AuditPage {
  items: AuditItem[];
  total: number;
  limit: number;
  offset: number;
}
interface Member {
  id: string;
  displayName: string;
  email: string;
}

const PAGE_SIZE = 50;

/** Kein Code-Styling im ui-kit vorhanden — lokal halten statt global einführen. */
const codeStyle: React.CSSProperties = {
  background: 'var(--au-surface-2, rgba(0,0,0,.25))',
  borderRadius: 'var(--au-radius-2, 6px)',
  padding: '.6rem',
  fontSize: '.75rem',
  maxHeight: '18rem',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'medium' });
}

/** `core.role.permissions_changed` → „Rolle · permissions changed" (lesbar, ohne Registry). */
function actionLabel(action: string): string {
  const parts = action.split('.');
  return parts.slice(1).join(' · ').replace(/_/g, ' ');
}

export default function AuditPage() {
  const [filter, setFilter] = useState({
    action: '',
    entityType: '',
    userId: '',
    from: '',
    to: '',
  });
  const [offset, setOffset] = useState(0);
  const [detail, setDetail] = useState<AuditItem | null>(null);

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (filter.action) params.set('action', filter.action);
  if (filter.entityType) params.set('entityType', filter.entityType);
  if (filter.userId) params.set('userId', filter.userId);
  // date-Inputs liefern YYYY-MM-DD → auf Tagesgrenzen erweitern.
  if (filter.from) params.set('from', new Date(`${filter.from}T00:00:00`).toISOString());
  if (filter.to) params.set('to', new Date(`${filter.to}T23:59:59`).toISOString());

  const log = useQuery({
    queryKey: ['audit', params.toString()],
    queryFn: () => api<AuditPage>(`/api/v1/audit?${params.toString()}`),
  });
  const options = useQuery({
    queryKey: ['audit', 'options'],
    queryFn: () => api<{ actions: string[]; entityTypes: string[] }>('/api/v1/audit/options'),
  });
  // Schlanke Mitgliederliste — sie darf jedes Mitglied sehen, anders als
  // /api/v1/users (das core.users.manage verlangt).
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => api<{ members: Member[] }>('/api/v1/members'),
  });

  const update = (patch: Partial<typeof filter>) => {
    setFilter({ ...filter, ...patch });
    setOffset(0); // Filterwechsel → zurück auf Seite 1
  };

  if (log.isError) {
    return (
      <div className="page">
        <h1 className="page__title">Audit-Log</h1>
        <EmptyState
          title="Kein Zugriff"
          description="Dir fehlt die Berechtigung „Audit-Log einsehen“."
        />
      </div>
    );
  }

  const total = log.data?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="page">
      <h1 className="page__title">Audit-Log</h1>
      <p className="page__lead">Wer hat was wann geändert — unveränderliche Historie.</p>

      <Card>
        <div className="crm-form__row">
          <Field label="Von">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={filter.from}
                onChange={(e) => update({ from: e.target.value })}
              />
            )}
          </Field>
          <Field label="Bis">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={filter.to}
                onChange={(e) => update({ to: e.target.value })}
              />
            )}
          </Field>
          <Field label="Urheber">
            {(id) => (
              <Select
                id={id}
                value={filter.userId}
                onChange={(e) => update({ userId: e.target.value })}
              >
                <option value="">— alle —</option>
                {(members.data?.members ?? []).map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.displayName}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Aktion">
            {(id) => (
              <Select
                id={id}
                value={filter.action}
                onChange={(e) => update({ action: e.target.value })}
              >
                <option value="">— alle —</option>
                {(options.data?.actions ?? []).map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Objekt">
            {(id) => (
              <Select
                id={id}
                value={filter.entityType}
                onChange={(e) => update({ entityType: e.target.value })}
              >
                <option value="">— alle —</option>
                {(options.data?.entityTypes ?? []).map((entityType) => (
                  <option key={entityType} value={entityType}>
                    {entityType}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Card>

      <Card>
        {log.isLoading ? (
          <Spinner />
        ) : total === 0 ? (
          <EmptyState
            title="Keine Einträge"
            description="Für diese Filter gibt es keine Vorgänge."
          />
        ) : (
          <>
            <table className="au-table">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Urheber</th>
                  <th>Aktion</th>
                  <th>Objekt</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {log.data!.items.map((item) => (
                  <tr key={item.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.createdAt)}</td>
                    <td>{item.userLabel ?? <span className="crm-timeline__meta">System</span>}</td>
                    <td>
                      <Badge>{actionLabel(item.action)}</Badge>
                      <div className="crm-timeline__meta">{item.action}</div>
                    </td>
                    <td>
                      {item.entityType ?? '—'}
                      {item.entityId && (
                        <div className="crm-timeline__meta">{item.entityId.slice(0, 18)}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Button size="sm" variant="subtle" onClick={() => setDetail(item)}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div
              className="crm-composer__row"
              style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}
            >
              <span className="crm-timeline__meta">
                {from}–{to} von {total}
              </span>
              <div style={{ display: 'flex', gap: '.5rem' }}>
                <Button
                  size="sm"
                  variant="subtle"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Zurück
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  disabled={to >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Weiter
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {detail && (
        <Dialog open wide aria-label="Vorgang im Detail" onClose={() => setDetail(null)}>
          <h2 className="crm-section__title">Vorgang im Detail</h2>
          <div className="crm-timeline__meta">
            {formatDateTime(detail.createdAt)} · {detail.userLabel ?? 'System'}
            {detail.ip ? ` · ${detail.ip}` : ''}
          </div>
          <p>
            <strong>{detail.action}</strong>
            {detail.entityType ? ` — ${detail.entityType} ${detail.entityId ?? ''}` : ''}
          </p>
          <div className="crm-detail__grid">
            <div>
              <div className="crm-section__title">Vorher</div>
              <pre style={codeStyle}>
                {detail.before ? JSON.stringify(detail.before, null, 2) : '—'}
              </pre>
            </div>
            <div>
              <div className="crm-section__title">Nachher</div>
              <pre style={codeStyle}>
                {detail.after ? JSON.stringify(detail.after, null, 2) : '—'}
              </pre>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
